import {
  CONSENT_CHANGED_EVENT,
  CONSENT_COOKIE_MAX_AGE_DAYS,
  CONSENT_COOKIE_NAME,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION
} from './constants';
import type { ConsentChoice, ConsentPreferences } from './types';

function encodeCookieValue(preferences: ConsentPreferences): string {
  return `v${CONSENT_VERSION}:${btoa(JSON.stringify(preferences))}`;
}

function decodeCookieValue(raw: string): ConsentPreferences | null {
  const prefix = `v${CONSENT_VERSION}:`;
  if (!raw.startsWith(prefix)) return null;

  try {
    const parsed = JSON.parse(atob(raw.slice(prefix.length))) as ConsentPreferences;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (parsed.necessary !== true || parsed.functional !== true) return null;
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.updatedAt !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeDays: number): void {
  if (typeof document === 'undefined') return;
  const maxAgeSeconds = maxAgeDays * 24 * 60 * 60;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}; Domain=.we-tree.app`;
}

function readLocalStorage(): ConsentPreferences | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentPreferences;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalStorage(preferences: ConsentPreferences): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
}

function canSyncCrossSubdomainCookie(): boolean {
  return typeof window !== 'undefined' && window.location.hostname.endsWith('we-tree.app');
}

export function getStoredConsent(): ConsentPreferences | null {
  const fromStorage = readLocalStorage();
  if (fromStorage) return fromStorage;

  const fromCookie = readCookie(CONSENT_COOKIE_NAME);
  if (!fromCookie) return null;

  const parsed = decodeCookieValue(fromCookie);
  if (parsed) {
    writeLocalStorage(parsed);
  }
  return parsed;
}

export function hasStoredConsent(): boolean {
  return getStoredConsent() !== null;
}

export function hasAnalyticsConsent(): boolean {
  return getStoredConsent()?.analytics === true;
}

export function saveConsent(choice: ConsentChoice, analyticsEnabled?: boolean): ConsentPreferences {
  const analytics =
    choice === 'accept-all' ? true : choice === 'reject-non-essential' ? false : (analyticsEnabled ?? false);

  const preferences: ConsentPreferences = {
    version: CONSENT_VERSION,
    necessary: true,
    functional: true,
    analytics,
    updatedAt: new Date().toISOString()
  };

  writeLocalStorage(preferences);

  if (canSyncCrossSubdomainCookie()) {
    try {
      writeCookie(CONSENT_COOKIE_NAME, encodeCookieValue(preferences), CONSENT_COOKIE_MAX_AGE_DAYS);
    } catch {
      // Domain cookie may fail on some environments; localStorage is sufficient.
    }
  }

  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: preferences }));
  return preferences;
}

export function onConsentChanged(listener: (preferences: ConsentPreferences) => void): () => void {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<ConsentPreferences>;
    if (customEvent.detail) {
      listener(customEvent.detail);
    }
  };

  window.addEventListener(CONSENT_CHANGED_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
}
